/**
 * The split auth layout.
 *
 * Transcribed from the mockup's `isAuth` block: a `minmax(400px, 42%) 1fr` grid, warm panel left,
 * form right, capped at 398px and centred.
 *
 * The left panel always states that an Account is optional. That is not marketing copy — it is the
 * product's central claim, and the sign-in screen is exactly where someone is most likely to assume
 * the opposite and give up.
 *
 * **Brand asset note:** the mockup uses `assets/lockup-primary.png` here. That file exceeds the
 * design tool's 256 KiB fetch cap and comes back truncated, so the shield and a typeset wordmark
 * stand in. Swap it for the real lockup when it reaches the repo — see 14-roadmap.md.
 */

import type { ReactNode } from 'react';

import './auth-layout.css';

export interface AuthLayoutProps {
  /** The tracked uppercase label above the heading. */
  eyebrow: string;
  /** Crimson Pro heading for this particular screen. */
  heading: string;
  /** One sentence under the heading. Kept short; the panel carries the argument. */
  blurb?: string | undefined;
  children: ReactNode;
}

export function AuthLayout({
  eyebrow,
  heading,
  blurb,
  children,
}: AuthLayoutProps): React.JSX.Element {
  return (
    <div className="auth-split">
      <aside className="auth-split__aside">
        <div>
          <div className="auth-lockup">
            <img className="auth-lockup__mark" src="/brand/shield.png" alt="" />
            <span className="auth-lockup__wordmark">
              PIUS <span className="auth-lockup__wordmark-second">SELECTIONS</span>
            </span>
          </div>

          <hr className="auth-split__rule" />

          <p className="auth-split__statement">
            One authoritative Planning Workspace for every celebration, with a history that explains
            how each decision was made.
          </p>

          <p className="auth-split__note">
            An Account is optional. Participants invited by Capability Link contribute without
            registering.
          </p>
        </div>

        <p className="auth-split__motto">
          For the greater glory of God and the beauty of His Church
        </p>
      </aside>

      <main className="auth-split__main">
        <div className="auth-split__form rise-in">
          <p className="auth-split__eyebrow">{eyebrow}</p>
          <h1 className="auth-split__heading">{heading}</h1>
          {blurb ? <p className="auth-split__blurb">{blurb}</p> : null}

          {children}
        </div>
      </main>
    </div>
  );
}

/**
 * The white card the fields sit in.
 *
 * A distinct surface in the mockup, not just spacing — it separates "the thing you fill in" from the
 * explanatory text around it.
 */
export function AuthCard({ children }: { children: ReactNode }): React.JSX.Element {
  return <div className="auth-card">{children}</div>;
}
